import { apiRequest } from './apiClient';

export type SpendAlertChannels = {
  push: boolean;
  email: boolean;
  sms: boolean;
};

export type SpendAlertIntent =
  | 'cut_dining'
  | 'cut_subscriptions'
  | 'cut_shopping'
  | 'cut_entertainment'
  | 'general';

export type ChannelReadiness = {
  push: { available: boolean; detail: string };
  email: { available: boolean; detail: string; value?: string };
  sms: { available: boolean; detail: string; value?: string };
};

export type SpendAlert = {
  id: string;
  categorySlug: string;
  categoryLabel: string;
  intent: SpendAlertIntent;
  dayOfWeek: number;
  dayLabel: string;
  hourLocal: number;
  timezoneOffsetMinutes: number;
  leadHours: number;
  fireHourLocal: number;
  channels: SpendAlertChannels;
  enabled: boolean;
  reason: string;
  avgAmount: number;
  sampleCount: number;
  lastFiredAt: string | null;
  createdAt: string;
};

export type SpendAlertSuggestion = {
  categorySlug: string;
  categoryLabel: string;
  dayOfWeek: number;
  dayLabel: string;
  hourLocal: number;
  hourInferred?: boolean;
  leadHours: number;
  fireHourLocal: number;
  timezoneOffsetMinutes: number;
  avgAmount: number;
  sampleCount: number;
  sharePct: number;
  reason: string;
  alreadyEnabled: boolean;
  source?: 'spend';
};

export async function listSpendAlerts(): Promise<{
  alerts: SpendAlert[];
  preferences: SpendAlertChannels;
  channels: ChannelReadiness;
}> {
  return apiRequest('/spend-alerts');
}

export async function suggestSpendAlerts(
  timezoneOffsetMinutes: number = new Date().getTimezoneOffset(),
): Promise<{
  suggestions: SpendAlertSuggestion[];
  channels: ChannelReadiness;
}> {
  return apiRequest(
    `/spend-alerts/suggestions?timezoneOffsetMinutes=${encodeURIComponent(String(timezoneOffsetMinutes))}`,
  );
}

export async function createSpendAlert(input: {
  categorySlug: string;
  categoryLabel?: string;
  intent?: SpendAlertIntent;
  dayOfWeek: number;
  hourLocal: number;
  timezoneOffsetMinutes: number;
  leadHours?: number;
  channels?: Partial<SpendAlertChannels>;
  reason?: string;
  avgAmount?: number;
  sampleCount?: number;
}): Promise<{ alert: SpendAlert; updated: boolean }> {
  return apiRequest('/spend-alerts', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function patchSpendAlert(
  id: string,
  patch: {
    enabled?: boolean;
    channels?: Partial<SpendAlertChannels>;
    leadHours?: number;
    intent?: SpendAlertIntent;
    categorySlug?: string;
    categoryLabel?: string;
    dayOfWeek?: number;
    hourLocal?: number;
    timezoneOffsetMinutes?: number;
    reason?: string;
  },
): Promise<{ alert: SpendAlert }> {
  return apiRequest(`/spend-alerts/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export async function deleteSpendAlert(id: string): Promise<{ ok: boolean }> {
  return apiRequest(`/spend-alerts/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export type SpendAlertChannelAttempt = {
  attempted: boolean;
  sent: boolean;
  detail: string;
};

export async function testSpendAlert(
  id: string,
  forceAll = true,
): Promise<{
  ok: boolean;
  forceAll: boolean;
  delivery: {
    push: SpendAlertChannelAttempt;
    email: SpendAlertChannelAttempt;
    sms: SpendAlertChannelAttempt;
  };
}> {
  return apiRequest(`/spend-alerts/${encodeURIComponent(id)}/test`, {
    method: 'POST',
    body: JSON.stringify({ forceAll }),
  });
}
